import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Upload, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function BusinessProfile() {
  const { business, refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (business) setForm({ ...business }); }, [business]);

  if (!form) return <div className="text-slate-400">Loading…</div>;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be under 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => set("logo", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/business-profile", form);
      await refresh();
      toast.success("Business profile saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="business-profile-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Business Profile</h1>
          <p className="text-slate-500 mt-1">Appears on all PDFs, receipts and reports</p>
        </div>
        <Button className="bg-maroon hover:bg-maroon-hover" onClick={save} disabled={saving} data-testid="save-business-button">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-5">
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
            {form.logo ? <img src={form.logo} alt="logo" className="h-full w-full object-contain" /> : <Building2 className="h-8 w-8 text-slate-300" />}
          </div>
          <div>
            <Label>Business Logo</Label>
            <div>
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-accentblue hover:underline mt-1">
                <Upload className="h-4 w-4" /> Upload logo
                <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="business-logo-input" />
              </label>
            </div>
            <p className="text-xs text-slate-400 mt-1">PNG/JPG, under 5MB</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Business Name</Label>
            <Input value={form.business_name || ""} onChange={(e) => set("business_name", e.target.value)} data-testid="business-name-input" /></div>
          <div><Label>Owner / Proprietor Name</Label>
            <Input value={form.owner_name || ""} onChange={(e) => set("owner_name", e.target.value)} data-testid="business-owner-input" /></div>
          <div><Label>Mobile Number</Label>
            <Input value={form.mobile || ""} onChange={(e) => set("mobile", e.target.value)} data-testid="business-mobile-input" /></div>
          <div><Label>Email</Label>
            <Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="business-email-input" /></div>
          <div><Label>GST Number (optional)</Label>
            <Input value={form.gst_number || ""} onChange={(e) => set("gst_number", e.target.value)} data-testid="business-gst-input" /></div>
          <div><Label>Money-Lending Licence No. (optional)</Label>
            <Input value={form.license_number || ""} onChange={(e) => set("license_number", e.target.value)} data-testid="business-license-input" /></div>
          <div className="sm:col-span-2"><Label>Business Address</Label>
            <Textarea value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="business-address-input" /></div>
          <div className="sm:col-span-2"><Label>Terms &amp; Conditions</Label>
            <Textarea rows={4} value={form.terms || ""} onChange={(e) => set("terms", e.target.value)} placeholder="Shown at the bottom of PDFs and receipts" data-testid="business-terms-input" /></div>
        </div>
      </div>
    </div>
  );
}
